import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { HORECA } from '@/constants/testIds';
import { ArrowUpRight } from 'lucide-react';
import CategoryModal from './CategoryModal';
import { API, resolveImg } from './adminConfig';

const FORMATS = [
  { size: '40 g', label: 'Adetlik' },
  { size: '1 kg', label: 'Paket' },
  { size: '3 kg', label: 'Blok' },
];

export default function ShanverB2B() {
  const [flavors, setFlavors] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/horeca/content`, { params: { section: 'sanver' } })
      .then((res) => setFlavors(res.data?.sanver || []))
      .catch(() => setFlavors([]));
  }, []);

  // İlk çeşit "hero" olarak seçilir (varsa 40 g görselini bir alt üründe arıyoruz)
  const hero = flavors[0];
  const heroForty =
    hero?.subcategories?.find((s) => /40\s*g/i.test(s.name)) || null;

  return (
    <section
      id="sanver"
      data-testid={HORECA.sanverSection}
      className="relative paper-kraft text-[#1F1F1F] py-24 md:py-32 overflow-hidden"
    >
      <div className="grain" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
        {/* Başlık */}
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 mb-14">
          <div className="lg:col-span-7">
            <span className="eyebrow text-[#B08834] block mb-4">Şanver 1933 · B2B Helva</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-[64px] leading-[1.02] tracking-[-0.01em] font-light">
              Toplu tüketime
              <span className="italic text-[#B03A2E]"> tahin helvası</span>.
            </h2>
          </div>
          <p className="lg:col-span-5 lg:pt-4 text-[#5A5145] text-base md:text-lg leading-relaxed font-body font-light">
            Lutfiye kataloğuna eklenen Şanver B2B helva serisi; otel açık büfeleri,
            restoran ikram menüleri ve toplu tüketim için üç ambalaj seçeneğiyle sunulur.
          </p>
        </div>

        {/* Feature: 40g adetlik + format rozetleri */}
        {hero && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-7 relative bg-[#1F1F1F] text-[#FBF7EE] dark-frame overflow-hidden flex flex-col md:flex-row items-center"
            >
              <div className="relative w-full md:w-1/2 bg-[#FFFDF7] flex items-center justify-center p-6 md:p-8 min-h-[320px]">
                {(heroForty?.image_url || hero.image_url) && (
                  <img
                    src={resolveImg(heroForty?.image_url || hero.image_url)}
                    alt={heroForty?.name || hero.name}
                    className="max-h-[300px] w-auto object-contain drop-shadow-xl"
                    loading="lazy"
                  />
                )}
                <span
                  className="absolute top-4 left-4 label-pill text-[#1F1F1F]"
                  style={{ background: hero.accent || '#B08834' }}
                >
                  40 g Adetlik
                </span>
              </div>
              <div className="w-full md:w-1/2 p-8 md:p-10 lg:p-12">
                <span className="eyebrow text-[#FBF7EE]/60">Porsiyonluk ikram</span>
                <h3 className="font-display text-3xl md:text-4xl leading-tight mt-3 mb-4">
                  40 g tekli paket
                </h3>
                <p className="text-sm md:text-base font-body font-light text-[#FBF7EE]/75 leading-relaxed">
                  Kahvaltı büfeleri ve ikram sunumları için hijyenik, tek kullanımlık
                  porsiyonlar. Koli bazlı toplu sevkiyata uygundur.
                </p>
                <a
                  href="#iletisim"
                  className="mt-8 inline-flex items-center gap-3 text-xs tracking-[0.2em] text-[#B08834] hover:text-[#FBF7EE] transition-colors font-body group"
                >
                  TOPLU FİYAT AL
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
              </div>
            </motion.div>

            {/* Format kartları */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-5 bg-[#FFFDF7] dark-frame p-8 md:p-10 flex flex-col justify-center"
            >
              <span className="eyebrow text-[#5A5145] mb-6">Ambalaj Seçenekleri</span>
              <div className="space-y-5">
                {FORMATS.map((f) => (
                  <div
                    key={f.size}
                    className="flex items-baseline justify-between pb-4 border-b border-[#1F1F1F]/12 last:border-0 last:pb-0"
                  >
                    <span className="font-display text-4xl md:text-5xl text-[#1F1F1F] leading-none">
                      {f.size}
                    </span>
                    <span className="eyebrow text-[#B08834]">{f.label}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-[#5A5145] font-body font-light mt-6 leading-relaxed">
                Tüm çeşitler 1 kg paket ve 3 kg blok olarak; sade çeşit ayrıca 40 g
                adetlik olarak temin edilebilir.
              </p>
            </motion.div>
          </div>
        )}

        {/* Çeşitler grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-2">
          {flavors.map((c, i) => (
            <motion.button
              key={c.id}
              type="button"
              onClick={() => setSelected(c)}
              data-testid={HORECA.sanverCard(c.id)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.7, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
              className="group relative bg-[#FBF7EE] dark-frame block overflow-hidden flex flex-col text-left"
            >
              <div className="relative bg-[#FFFDF7] aspect-[4/3] overflow-hidden flex items-center justify-center p-6">
                {c.image_url && (
                  <img
                    src={resolveImg(c.image_url)}
                    alt={c.name}
                    className="max-h-full max-w-full object-contain transition-transform duration-[1200ms] group-hover:scale-105"
                    loading="lazy"
                  />
                )}
              </div>
              <div
                className="px-6 py-5 border-t-2 flex items-start justify-between gap-3 flex-1"
                style={{ borderColor: c.accent || '#B08834' }}
              >
                <div className="flex-1">
                  <h3 className="font-display text-xl md:text-2xl leading-tight text-[#1F1F1F]">
                    {c.name}
                  </h3>
                  <div className="mt-2 flex items-center gap-3 flex-wrap">
                    {c.tag && <span className="eyebrow text-[#5A5145]">{c.tag}</span>}
                    <span className="eyebrow text-[#B08834]">1 kg · 3 kg</span>
                  </div>
                </div>
                <ArrowUpRight className="w-5 h-5 text-[#1F1F1F]/60 group-hover:text-[#B03A2E] transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 flex-shrink-0 mt-1" />
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <CategoryModal category={selected} onClose={() => setSelected(null)} />
    </section>
  );
}
